<template>
  <div
    class="my-orders__item"
    :class="[{ active: isDetailsShown }, order.statuses[0].status]"
  >
    <div
      class="my-orders__item-container"
      :class="{ active: isDetailsShown }"
      @click="toggleShowDetails"
    >
      <div class="my-orders__item-header">
        <p class="my-orders__item-number desktop">№ {{ order.number }}</p>
        <div class="my-orders__item-header-w">
          <span class="my-orders__item-number mobile">
            № {{ order.number }}
          </span>
          <span class="my-orders__item-date mobile">
            {{ _T("@From date") }} {{ normalizeDate(order.createdAt) }}
          </span>
        </div>
        <span
          class="my-orders__arrow mobile"
          :class="{ active: isDetailsShown }"
        ></span>
      </div>
      <div class="my-orders__width-status">
        <OrderStatus :statuses="order.statuses" />
      </div>
      <p class="my-orders__item-date desktop">
        {{ normalizeDate(order.createdAt) }}
      </p>
      <p class="my-orders__item-sum">
        {{ priceFormatter(order.totalPrice.amount) }} &#8372;
      </p>
      <div class="my-orders__item-products" :class="{ active: isDetailsShown }">
        <div class="my-orders__img-box">
          <img
            v-for="(product, index) in reduceProducts(order.products)"
            :key="index"
            :src="product.getMainImage('small')"
            :alt="cleanStringsConcat([product.getName(), 'image'])"
          />
        </div>
        <p v-if="order.products.length > 4" class="my-orders__unique-items">
          +{{ order.products.length - 4 }}
        </p>
        <span
          class="my-orders__arrow desktop"
          :class="{ active: isDetailsShown }"
        ></span>
      </div>
    </div>
    <Transition name="expand-item">
      <OrderInfo v-if="isDetailsShown" :order="order" />
    </Transition>
  </div>
</template>

<script setup>
import OrderInfo from "~/modules/account/ui/components/orders/OrderInfo.vue";
import OrderStatus from "~/modules/account/ui/components/orders/OrderStatus.vue";
import { Product } from "~/models/product.model";

const props = defineProps({
  order: { type: Object, required: false },
});

const isDetailsShown = ref(false);

function toggleShowDetails() {
  isDetailsShown.value = !isDetailsShown.value;
}

const reduceProducts = (products) => {
  return products.slice(0, 4).map(({ product }) => new Product(product));
};
</script>

<style lang="scss" scoped>
.my-orders {
  &__item {
    border: 1px solid var(--color-sky-base);
    border-radius: 8px;

    &:not(:last-of-type) {
      margin-bottom: 8px;
    }

    @include mobile {
      border-radius: 8px;
    }
  }

  &__item-container {
    display: grid;
    grid-template-columns: 1.5fr 2fr 1fr 1fr 2fr;
    grid-template-rows: 1fr;
    grid-column-gap: 5px;

    align-items: center;
    text-align: center;
    padding: 16px;

    cursor: pointer;

    &.active {
      border-bottom: 1px solid var(--color-sky-base);
      @include mobile {
        border: none;
      }
    }

    @include mobile {
      @include flex-container(column, flex-start, flex-start);
      gap: 8px;
    }
  }

  &__item-header {
    @include mobile {
      width: 100%;
      @include flex-container(row, space-between, center);

      & span {
        display: inline;
      }
    }
  }

  &__item-header-w {
    @include flex-container(row, flex-start, center);
    flex-wrap: wrap;
    gap: 8px;
  }

  &__item-number {
    color: var(--color-ink-base);
    text-align: left;
    @include font(16, 22, 500);

    &.mobile {
      display: none;

      @include mobile {
        display: inline;
      }
    }

    &.desktop {
      @include mobile {
        display: none;
      }
    }

    @include mobile {
      @include font(16, 22, 700);
    }
  }

  &__item-date {
    color: var(--color-sky-darkest);
    @include font(16, 22, 400);

    &.mobile {
      display: none;

      @include mobile {
        display: inline;
        color: var(--color-ink-base);
      }
    }

    &.desktop {
      @include mobile {
        display: none;
      }
    }
  }

  &__item-sum {
    color: var(--color-primary-base);
    @include font(18, 24, 700);
  }

  &__item-products {
    @include flex-container(row, space-between, center);

    &.active {
      @include mobile {
        display: none;
      }
    }
  }

  &__img-box {
    @include flex-container(row, flex-start, center);
    flex-wrap: wrap;

    & img {
      width: 40px;
      height: 40px;

      @include mobile {
        width: 48px;
        height: 48px;
      }

      &:not(:last-of-type) {
        margin-right: 8px;

        @include mobile {
          margin-right: 16px;
        }
      }
    }
  }

  &__unique-items {
    @include font(16, 20, 700);
    color: var(--color-primary-base);
    margin: 0 8px;

    @include mobile {
      margin: 0 16px;
    }
  }

  &__arrow {
    cursor: pointer;
    display: inline-block;

    border: solid var(--color-primary-base);
    border-width: 0 2px 2px 0;
    padding: 4px;

    transition: all 0.4s ease-in-out;
    transform: rotate(45deg);

    &.mobile {
      display: none;
      @include mobile {
        display: inline-block;
      }
    }

    &.desktop {
      @include mobile {
        display: none;
      }
    }

    &.active {
      transform: rotate(225deg);
    }
  }

  &__item-line {
    width: 100%;
    height: 1px;

    display: flex;
    background-color: var(--color-sky-base);

    @include mobile {
      display: none;
    }
  }
}
</style>
